import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CHARACTER_STATE_COLORS } from '../../utils/stateHelpers';
import s from './SetupScreen.module.css';

const MAX_CUSTOM = 5;

function useFilterOptions() {
  const { t } = useTranslation();
  return [
    { key: 'all', label: t('workbook.all') },
    { key: 'to_learn', label: t('common.new') },
    { key: 'practicing', label: t('common.practicing') },
    { key: 'known', label: t('common.known') },
  ];
}

/* Mirrors StatusBadge from CreateWorksheetFlow */
function StatusBadge({ status }) {
  const color = CHARACTER_STATE_COLORS[status] || CHARACTER_STATE_COLORS.to_learn;
  let icon;
  if (status === 'known') {
    icon = (
      <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
        <path d="M1 4L3.5 6.5L9 1" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  } else if (status === 'practicing') {
    icon = (
      <svg width="15" height="11" viewBox="0 0 15 11" fill="none">
        <path d="M14.4885 4.07154V8.35718M3.29702 5.85722V8.35718C3.29702 8.92549 3.73918 9.47053 4.52624 9.87238C5.31329 10.2742 6.38077 10.5 7.49383 10.5C8.60689 10.5 9.67437 10.2742 10.4614 9.87238C11.2485 9.47053 11.6906 8.92549 11.6906 8.35718V5.85722M14.0829 4.73018C14.2081 4.67377 14.3143 4.58109 14.3885 4.46362C14.4626 4.34614 14.5014 4.20904 14.5 4.06929C14.4985 3.92954 14.457 3.79329 14.3805 3.6774C14.304 3.56152 14.1959 3.4711 14.0696 3.41735L8.07442 0.628824C7.89216 0.543932 7.69418 0.5 7.49386 0.5C7.29354 0.5 7.09556 0.543932 6.9133 0.628824L0.918857 3.41449C0.794329 3.47018 0.688393 3.56173 0.614005 3.67793C0.539617 3.79412 0.5 3.92994 0.5 4.06876C0.5 4.20759 0.539617 4.34341 0.614005 4.4596C0.688393 4.5758 0.794329 4.66734 0.918857 4.72304L6.9133 7.51442C7.09556 7.59931 7.29354 7.64324 7.49386 7.64324C7.69418 7.64324 7.89216 7.59931 8.07442 7.51442L14.0829 4.73018Z" stroke={color} strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  } else {
    icon = (
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path d="M6 1L7.3 4.2L10.8 4.5L8.1 6.7L8.9 10L6 8.2L3.1 10L3.9 6.7L1.2 4.5L4.7 4.2Z" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  return (
    <div className={s.statusBadge} style={{ border: `1px solid ${color}` }}>
      {icon}
    </div>
  );
}

export default function SetupScreen({ characters, onStart, onBack, initialCustomChars }) {
  const { t } = useTranslation();
  const FILTER_OPTIONS = useFilterOptions();

  // Pool selection view state
  const [selectedPool, setSelectedPool] = useState('all');

  // Sub-view: 'pools' | 'custom'
  const [subView, setSubView] = useState('pools');

  // Custom picker state
  const [customSelectedIds, setCustomSelectedIds] = useState(new Set());
  const [customFilter, setCustomFilter] = useState('all');
  const [customSearch, setCustomSearch] = useState('');

  // If arriving from character profile with pre-seeded chars, open custom sub-view
  useEffect(() => {
    if (initialCustomChars?.length > 0) {
      const initSet = new Set(
        initialCustomChars.map((c) => c.id || c.character_id || c.char)
      );
      setCustomSelectedIds(initSet);
      setSubView('custom');
    }
  }, [initialCustomChars]);

  const POOLS = useMemo(() => ([
    {
      key: 'all',
      label: t('practice.poolAll'),
      // "All" = everything practiceable: to_learn + practicing. known is
      // excluded (mastered chars are not part of the writing practice pool —
      // the dedicated "known" pool below is for explicit review). A learner who
      // has only read one story (all chars still to_learn) lands here with a
      // non-empty pool. (FP-PRACTICE-POOL-01)
      filter: (c) => c.state === 'to_learn' || c.state === 'practicing',
      subtext: (count) => t('practice.poolCountCharacters', { count }),
    },
    {
      key: 'practicing',
      label: t('practice.poolPracticing'),
      filter: (c) => c.state === 'practicing',
      subtext: (count) => t('practice.poolCountInPractice', { count }),
    },
    {
      key: 'new',
      label: t('practice.poolNew'),
      filter: (c) => c.state === 'to_learn',
      subtext: (count) => t('practice.poolCountNew', { count }),
    },
    {
      key: 'known',
      label: t('practice.poolKnown'),
      filter: (c) => c.state === 'known',
      subtext: () => t('practice.poolCountKnown'),
    },
  ]), [t]);

  const poolCounts = useMemo(() => {
    const counts = {};
    for (const pool of POOLS) {
      counts[pool.key] = characters.filter(pool.filter).length;
    }
    return counts;
  }, [characters, POOLS]);

  const selectedChars = useMemo(() => {
    const pool = POOLS.find((p) => p.key === selectedPool);
    return characters.filter(pool.filter);
  }, [characters, selectedPool, POOLS]);

  const canStart = selectedChars.length > 0;

  // Custom picker: filtered character list
  const customFilteredChars = useMemo(() => {
    let list = characters;
    if (customFilter !== 'all') list = list.filter((c) => c.state === customFilter);
    if (customSearch.trim()) {
      const q = customSearch.trim().toLowerCase();
      list = list.filter((c) =>
        (c.char || '').includes(q) ||
        (c.simplified_char || '').includes(q) ||
        (c.pinyin || '').toLowerCase().includes(q) ||
        (c.meaning || c.definition_en || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [characters, customFilter, customSearch]);

  // Custom picker: characters corresponding to selected IDs (for onStart)
  const customSelectedChars = useMemo(
    () => characters.filter((c) => customSelectedIds.has(c.id || c.character_id || c.char)),
    [characters, customSelectedIds]
  );

  function toggleCustomChar(char) {
    const key = char.id || char.character_id || char.char;
    setCustomSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else if (next.size < MAX_CUSTOM) {
        next.add(key);
      }
      return next;
    });
  }

  function handleEnterCustom() {
    setSubView('custom');
  }

  function handleBackFromCustom() {
    setSubView('pools');
    setCustomSearch('');
  }

  // ─── Custom picker sub-view ───
  if (subView === 'custom') {
    return (
      <div className={s.setup}>
        <button
          type="button"
          className={s.backButton}
          onClick={handleBackFromCustom}
          aria-label={t('practice.backAriaLabel')}
        >
          {t('practice.back')}
        </button>

        <header className={s.header}>
          <h1 className={s.title}>{t('practice.customTitle')}</h1>
          <p className={s.subtitle}>{t('practice.customSubtitle')}</p>
        </header>

        <div className={s.customControls}>
          <input
            type="text"
            className={s.searchInput}
            placeholder={t('worksheetCreate.searchPlaceholder')}
            value={customSearch}
            onChange={(e) => setCustomSearch(e.target.value)}
          />
          <select
            className={s.filterSelect}
            value={customFilter}
            onChange={(e) => setCustomFilter(e.target.value)}
          >
            {FILTER_OPTIONS.map((opt) => (
              <option key={opt.key} value={opt.key}>{opt.label}</option>
            ))}
          </select>
        </div>

        <p className={s.selectedCount}>
          {t('practice.customSelectedCount', { count: customSelectedIds.size, max: MAX_CUSTOM })}
        </p>

        <div className={s.charGrid}>
          {customFilteredChars.length === 0 ? (
            <p className={s.emptyText}>{t('worksheetCreate.noCharsFound')}</p>
          ) : (
            customFilteredChars.map((char) => {
              const key = char.id || char.character_id || char.char;
              const isSelected = customSelectedIds.has(key);
              const isDisabled = !isSelected && customSelectedIds.size >= MAX_CUSTOM;
              return (
                <button
                  key={key}
                  type="button"
                  className={`${s.charCard} ${isSelected ? s.charCardSelected : ''}`}
                  onClick={() => toggleCustomChar(char)}
                  disabled={isDisabled}
                  aria-pressed={isSelected}
                >
                  <StatusBadge status={char.state || 'to_learn'} />
                  <span className={s.charCardChar}>{char.char || char.simplified_char}</span>
                  {char.pinyin && <span className={s.charCardPinyin}>{char.pinyin}</span>}
                  {(char.meaning || char.definition_en) && (
                    <span className={s.charCardMeaning}>{char.meaning || char.definition_en}</span>
                  )}
                </button>
              );
            })
          )}
        </div>

        <div className={s.actions}>
          <button
            type="button"
            className={s.startButton}
            onClick={() => onStart(customSelectedChars)}
            disabled={customSelectedIds.size === 0}
          >
            {t('practice.startPracticing')}
          </button>
        </div>
      </div>
    );
  }

  // ─── Pool selection view (default) ───
  return (
    <div className={s.setup}>
      <button
        type="button"
        className={s.backButton}
        onClick={onBack}
        aria-label={t('practice.backAriaLabel')}
      >
        {t('practice.back')}
      </button>

      <header className={s.header}>
        <h1 className={s.title}>{t('practice.title')}</h1>
        <p className={s.subtitle}>{t('practice.subtitle')}</p>
      </header>

      <div className={s.poolList}>
        {POOLS.map((pool) => {
          const count = poolCounts[pool.key];
          const disabled = count === 0;
          const active = selectedPool === pool.key;

          return (
            <button
              key={pool.key}
              type="button"
              className={`${s.poolCard} ${active ? s.active : ''} ${disabled ? s.disabled : ''}`}
              onClick={() => !disabled && setSelectedPool(pool.key)}
              disabled={disabled}
              aria-pressed={active}
            >
              <span className={s.poolLabel}>{pool.label}</span>
              <span className={s.poolCount}>{pool.subtext(count)}</span>
            </button>
          );
        })}

        {/* Custom pool card — always enabled, tapping enters picker sub-view */}
        <button
          type="button"
          className={s.poolCard}
          onClick={handleEnterCustom}
          aria-label={t('practice.customAriaLabel')}
        >
          <span className={s.poolLabel}>{t('practice.poolCustom')}</span>
          <span className={s.poolCount}>{t('practice.poolCustomSubtext')}</span>
        </button>
      </div>

      <div className={s.actions}>
        <button
          type="button"
          className={s.startButton}
          onClick={() => onStart(selectedChars)}
          disabled={!canStart}
        >
          {t('practice.startPracticing')}
        </button>
      </div>
    </div>
  );
}
